import CardBrandIcon from "@/components/pages/AppPages/Dashboard/Wallet/CardBrandIcon";
import { Box } from "@mui/material";
import { styled } from "@mui/material";
import { capitalize } from "lodash";
import Stripe from "stripe";

import AddressForm, { BillingAddressCheckbox } from "./shipping/AddressForm";
import { Address, CheckoutIntent } from "../../types/CheckoutIntent";
import { useEffect, useState } from "react";
import { useCheckoutUI } from "../../CheckoutUIProvider";
import { SolidButton } from "@/components/StoreComponents/StoreButton";
import { TABLET_SCREEN_SIZE } from "@brandclub/common-ui";
import {
  setBillingAddressSameAsShipping,
  updateBillingAddress,
} from "@/api/rest/checkoutApi";

const PaymentMethodDisplayContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: 22,
  fontSize: 16,
  gap: 14,
  [theme.breakpoints.down("md")]: {
    fontSize: 14,
    gap: 8,
  },
}));

const BackfillBillingAddressContainer = styled(Box)(({ theme }) => ({
  marginTop: 16,
  ".buttonGroup": {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "22px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      marginTop: "12px",
      gap: "12px",
      flexDirection: "column-reverse",
    },
  },
}));

export const BackfillBillingAddress = () => {
  const [state, send] = useCheckoutUI();
  const checkoutIntent = state?.context?.checkoutIntent;
  const [useShippingAddress, setUseShippingAddress] = useState(true);
  const [address, setAddress] = useState<Partial<Address>>({});
  const [isProcessing, setIsProcessing] = useState(false);

  const onUseShippingAddressChange = (sameAsShipping: boolean) => {
    setUseShippingAddress(sameAsShipping);
    if (sameAsShipping && checkoutIntent?.shippingAddress) {
      setAddress(checkoutIntent.shippingAddress as Partial<Address>);
    }
  };

  useEffect(() => {
    if (useShippingAddress && checkoutIntent?.shippingAddress) {
      setAddress(checkoutIntent.shippingAddress as Partial<Address>);
    }
  }, [checkoutIntent?.shippingAddress, useShippingAddress]);

  const onAddressChange = (address: Partial<Address>) => {
    if (useShippingAddress) {
      setUseShippingAddress(false);
    }
    setAddress(address);
  };

  // const onCancel = () => {
  //   setAddress({});
  // };

  const handleSubmit = async () => {
    try {
      setIsProcessing(true);
      const checkoutIntentId = checkoutIntent?.checkoutIntentId;
      if (!checkoutIntentId) {
        setIsProcessing(false);
        return;
      }

      if (useShippingAddress) {
        const result = await setBillingAddressSameAsShipping({
          checkoutIntentId,
          sameAsShipping: true,
        });

        if (result?.status === "success" && result.checkoutIntent) {
          send({
            type: "SET_CHECKOUT_INTENT",
            data: { checkoutIntent: result.checkoutIntent },
          });
        } else {
          console.error(
            result?.errorMessage ||
              "Failed to set billing address same as shipping"
          );
        }
      } else {
        const result = await updateBillingAddress({
          checkoutIntentId,
          billingAddress: (address as CheckoutIntent["billingAddress"])!,
          skipValidation: false,
        });

        if (result?.status === "success" && result.checkoutIntent) {
          send({
            type: "SET_CHECKOUT_INTENT",
            data: { checkoutIntent: result.checkoutIntent },
          });
          setAddress({});
        } else {
          console.error(
            result?.errorMessage || "Failed to update billing address"
          );
        }
      }
      setIsProcessing(false);
    } catch (error) {
      console.error(error);
      setIsProcessing(false);
    }
  };

  const handlePaymentSubmit = () => {
    handleSubmit();
  };

  return (
    <BackfillBillingAddressContainer>
      <div style={{ marginBottom: "24px" }}>
        <AddressForm value={address} onChange={onAddressChange} />
      </div>
      <BillingAddressCheckbox
        useShippingAddress={useShippingAddress}
        onUseShippingAddressChange={onUseShippingAddressChange}
      />
      <div className="buttonGroup">
        <div></div>
        <SolidButton
          height={{ xs: 37 }}
          sx={{
            width: "unset",
            minWidth: "83px",
            [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
              width: "100%",
              maxWidth: "100%",
            },
          }}
          onClick={handlePaymentSubmit}
          disabled={isProcessing}
        >
          {isProcessing ? "Saving" : "Save"}
        </SolidButton>
      </div>
    </BackfillBillingAddressContainer>
  );
};

export const PaymentMethodDisplay = ({
  paymentMethod,
}: {
  paymentMethod: Stripe.PaymentMethod;
}) => {
  if (paymentMethod.type === "card") {
    return (
      <PaymentMethodDisplayContainer>
        <CardBrandIcon
          cardBrand={paymentMethod.card?.brand}
          walletType={paymentMethod.card?.wallet?.type}
          width={30}
          height={30}
        />
        {capitalize(paymentMethod.card?.brand ?? "Card")} ending in{" "}
        {paymentMethod.card?.last4}
      </PaymentMethodDisplayContainer>
    );
  } else if (paymentMethod.type === "link") {
    return (
      <PaymentMethodDisplayContainer>
        <LinkIcon />
        Link
      </PaymentMethodDisplayContainer>
    );
  }
  return <div>{capitalize(paymentMethod.type)}</div>;
};

const LinkIcon = () => {
  return (
    <svg
      className="SVGInline-svg SVGInline--cleaned-svg SVG-svg BrandIcon-svg BrandIcon--size--16-svg"
      height={20}
      width={20}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path fill="#00D66F" d="M0 0h32v32H0z"></path>
      <path
        fill="#011E0F"
        d="M15.144 6H10c1 4.18 3.923 7.753 7.58 10C13.917 18.246 11 21.82 10 26h5.144c1.275-3.867 4.805-7.227 9.142-7.914v-4.18c-4.344-.68-7.874-4.04-9.142-7.906Z"
      ></path>
    </svg>
  );
};
